import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { loadRazorpay } from "@/lib/razorpay-loader";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Heart, CheckCircle, AlertCircle } from "lucide-react";

interface DonateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PRESET_AMOUNTS = [99, 199, 499, 999];

const DonateDialog = ({ open, onOpenChange }: DonateDialogProps) => {
  
  const [amount, setAmount] = useState<number | "">("");
  const [customAmount, setCustomAmount] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const selectedAmount = typeof amount === "number" ? amount : Number(customAmount) || 0;

  const handlePresetClick = (val: number) => {
    setAmount(val);
    setCustomAmount("");
    setError("");
  };

  const handleCustomChange = (val: string) => {
    const num = val.replace(/\D/g, "");
    setCustomAmount(num);
    setAmount("");
    setError("");
  };

  const handleDonate = async () => {
    if (selectedAmount < 10) {
      setError("Minimum donation is ₹10");
      return;
    }
    if (selectedAmount > 100000) {
      setError("Maximum donation is ₹1,00,000");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(
        "/.netlify/functions/create-order",
        { amount: selectedAmount },
        { headers: { "Content-Type": "application/json" } }
      );

      const order = response.data.data;
      if (!order?.id) throw new Error("Razorpay order creation failed");

      const options = {
        key: import.meta.env.VITE_RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        name: "VRRentals",
        description: "Support & Donation",
        order_id: order.id,
        handler: async (res: any) => {
          console.log("Donation Payment Success:", res);
          try {
            await axios.post(
              "/.netlify/functions/verify-payment",
              {
                ...res,
                user_email: email || undefined,
                user_name: name || "Anonymous Supporter",
              },
              { headers: { "Content-Type": "application/json" } }
            );
            setSuccess(true);
          } catch (err) {
            console.error("Donation verification failed:", err);
            setSuccess(true);
          } finally {
            setLoading(false);
          }
        },
        prefill: {
          name: name || undefined,
          email: email || undefined,
        },
        theme: { color: "#932c9e" },
        modal: {
          ondismiss: () => setLoading(false),
        },
      };

      onOpenChange(false);
      setTimeout(async () => {
        const RazorpayConstructor = await loadRazorpay();
        const rzp = new RazorpayConstructor(options);
        rzp.open();
      }, 100);
    } catch (err) {
      console.error("Donation order creation failed:", err);
      setError("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  const handleClose = (val: boolean) => {
    if (!val) {
      setAmount("");
      setCustomAmount("");
      setName("");
      setEmail("");
      setError("");
      setSuccess(false);
      setLoading(false);
    }
    onOpenChange(val);
  };

  if (success) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="sr-only">Thank You</DialogTitle>
            <DialogDescription className="sr-only">Donation successful</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center py-8 gap-4 text-center">
            <CheckCircle className="w-16 h-16 text-secondary" />
            <h3 className="font-heading text-2xl font-bold text-foreground">
              Thank You! 🎉
            </h3>
            <p className="font-body text-muted-foreground text-sm">
              Your support means the world to us. We'll keep bringing amazing VR experiences!
            </p>
            <button
              onClick={() => handleClose(false)}
              className="mt-4 font-heading text-sm font-semibold px-8 py-3 rounded-full bg-primary text-primary-foreground glow-magenta"
            >
              Done
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-foreground flex items-center gap-2">
            <Heart className="w-5 h-5 text-primary" />
            Support VRRentals
          </DialogTitle>
          <DialogDescription className="font-body text-muted-foreground">
            Love what we do? Your donation helps us expand VR experiences to more people.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* Preset Amounts */}
          <div className="space-y-2">
            <Label className="font-body text-sm text-foreground">Choose an amount</Label>
            <div className="grid grid-cols-4 gap-2">
              {PRESET_AMOUNTS.map((val) => (
                <button
                  key={val}
                  onClick={() => handlePresetClick(val)}
                  className={`font-heading text-sm font-semibold py-3 rounded-lg border transition-all ${
                    amount === val
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border bg-muted text-foreground hover:border-primary/40"
                  }`}
                >
                  ₹{val}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Amount */}
          <div className="space-y-1.5">
            <Label htmlFor="customAmount" className="font-body text-sm text-foreground">
              Or enter custom amount
            </Label>
            <div className="flex">
              <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-border bg-muted text-muted-foreground text-sm">
                ₹
              </span>
              <Input
                id="customAmount"
                placeholder="Enter amount"
                value={customAmount}
                onChange={(e) => handleCustomChange(e.target.value)}
                className="rounded-l-none bg-muted border-border text-foreground placeholder:text-muted-foreground"
              />
            </div>
          </div>

          {/* Optional Name */}
          <div className="space-y-1.5">
            <Label htmlFor="donateName" className="font-body text-sm text-foreground">
              Name <span className="text-muted-foreground text-xs">(optional)</span>
            </Label>
            <Input
              id="donateName"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              className="bg-muted border-border text-foreground placeholder:text-muted-foreground"
            />
          </div>

          {/* Optional Email */}
          <div className="space-y-1.5">
            <Label htmlFor="donateEmail" className="font-body text-sm text-foreground">
              Email <span className="text-muted-foreground text-xs">(optional, for receipt)</span>
            </Label>
            <Input
              id="donateEmail"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={255}
              className="bg-muted border-border text-foreground placeholder:text-muted-foreground"
            />
          </div>

          {error && (
            <p className="text-xs text-destructive flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> {error}
            </p>
          )}

          {/* Donate Button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            onClick={handleDonate}
            disabled={loading || selectedAmount < 10}
            className="w-full font-heading text-base font-semibold px-8 py-4 rounded-full bg-primary text-primary-foreground glow-magenta transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <Heart className="w-4 h-4" />
            {loading
              ? "Processing..."
              : selectedAmount >= 10
                ? `Donate ₹${selectedAmount.toLocaleString("en-IN")}`
                : "Select an amount"}
          </motion.button>

          <p className="text-xs text-muted-foreground text-center font-body">
            Payments are securely processed via Razorpay.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DonateDialog;
